import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PublicHeader from './shared/PublicHeader';
import PublicFooter from './shared/PublicFooter';
import './PublicHome.css';

// Eight values that spell FIREWALL, laid out as two courses of a brick wall. Each brick
// carries a small security-themed illustration drawn for that value.
const FIREWALL_VALUES = [
  { letter: 'F', icon: 'flame', title: 'Follow your passion', lines: ['Do it with passion or not at all.', 'Go the extra mile.'] },
  { letter: 'I', icon: 'shield', title: 'Integrity', lines: ['Non-negotiable.', 'Integral to every aspect of the business.'] },
  { letter: 'R', icon: 'summit', title: 'Reach for glory', lines: ['Build for scale.', 'Through an elite workforce.'] },
  { letter: 'E', icon: 'bulb', title: 'Encourage innovation', lines: ['Never say no to an idea.', 'Make mistakes, but get things going.'] },
  { letter: 'W', icon: 'nodes', title: 'We work and win in teams', lines: ['Struggle and celebrate together.', 'Display humility when opinions differ.'] },
  { letter: 'A', icon: 'bolt', title: 'Act decisively', lines: ['Be accountable.', 'Commit to the uncomfortable.'] },
  { letter: 'L', icon: 'heart', title: 'Live for customer delight', lines: ['Customer first.', 'Going above and beyond.'] },
  { letter: 'L', icon: 'flag', title: 'Lead with example', lines: ['Performance matters.', 'Deliver consistent quality results.'] }
];

// One stroke colour, 48-unit grid. Kept simple so they read at 40px.
function ValueIcon({ name }) {
  const c = { width: 40, height: 40, viewBox: '0 0 48 48', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', className: 'value-icon', 'aria-hidden': true };
  switch (name) {
    case 'flame':
      return (<svg {...c}><path d="M24 6 C24 16, 14 18, 14 28 a10 10 0 0 0 20 0 c0 -6 -4 -8 -4 -12 c-2 3 -4 4 -6 4 C26 16 26 10 24 6 Z" /><path d="M24 40 c-3 0 -5 -2 -5 -5 c0 -3 3 -4 5 -8 c2 4 5 5 5 8 c0 3 -2 5 -5 5 Z" /></svg>);
    case 'shield':
      return (<svg {...c}><path d="M24 5 L40 11 V22 C40 32 33 39 24 43 C15 39 8 32 8 22 V11 Z" /><path d="M17 24 L22 29 L31 19" /></svg>);
    case 'summit':
      return (<svg {...c}><path d="M4 40 L18 18 L26 30 L32 22 L44 40 Z" /><path d="M32 22 V8" /><path d="M32 8 H42 L38 12 L42 16 H32" /></svg>);
    case 'bulb':
      return (<svg {...c}><path d="M24 5 a12 12 0 0 0 -7 21.5 c2 1.7 3 3.5 3 5.5 h8 c0 -2 1 -3.8 3 -5.5 A12 12 0 0 0 24 5 Z" /><path d="M20 37 h8 M21 41 h6" /><path d="M24 14 v6 l4 3" /></svg>);
    case 'nodes':
      return (<svg {...c}><circle cx="24" cy="10" r="4" /><circle cx="10" cy="36" r="4" /><circle cx="38" cy="36" r="4" /><path d="M21 13.5 L13 32.5 M27 13.5 L35 32.5 M14 36 H34" /></svg>);
    case 'bolt':
      return (<svg {...c}><path d="M27 4 L10 27 H23 L21 44 L38 20 H25 Z" /></svg>);
    case 'heart':
      return (<svg {...c}><path d="M24 41 C14 33 6 27 6 18 a8 8 0 0 1 14 -5 l4 4 l4 -4 a8 8 0 0 1 14 5 c0 9 -8 15 -18 23 Z" /></svg>);
    case 'flag':
      return (<svg {...c}><path d="M12 44 V6" /><path d="M12 8 H36 L30 16 L36 24 H12" /></svg>);
    default:
      return null;
  }
}

const PublicHome = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = React.useRef(null);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobFilters, setJobFilters] = useState({ workMode: '', experienceLevel: '', employmentType: '' });
  const [showTalentForm, setShowTalentForm] = useState(false);
  const [talentForm, setTalentForm] = useState({ name: '', email: '', phone: '', area: '', resume: null, message: '' });
  const [submittingTalent, setSubmittingTalent] = useState(false);
  const [talentSubmitted, setTalentSubmitted] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Filter videos based on supported platforms
  useEffect(() => {
    if (!settings?.employeeSpotlight?.videos) {
      setFilteredVideos([]);
      return;
    }

    const filtered = settings.employeeSpotlight.videos
      .filter(video => {
        if (!video.videoUrl) return false;
        return video.videoUrl.includes('youtube.com') ||
               video.videoUrl.includes('youtu.be') ||
               video.videoUrl.includes('vimeo.com') ||
               video.videoUrl.includes('linkedin.com');
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    setFilteredVideos(filtered);
  }, [settings?.employeeSpotlight?.videos]);

  // Auto-scroll slider every 5 seconds (only for 2-3 videos in slider mode)
  useEffect(() => {
    if (filteredVideos.length <= 1 || filteredVideos.length > 3) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const nextSlide = (prev + 1) % filteredVideos.length;
        scrollToSlide(nextSlide);
        return nextSlide;
      });
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [filteredVideos]);

  const scrollToSlide = (index) => {
    if (sliderRef.current) {
      const slideWidth = sliderRef.current.children[0]?.offsetWidth || 0;
      const gap = 24; // 1.5rem gap
      sliderRef.current.scrollTo({
        left: (slideWidth + gap) * index,
        behavior: 'smooth'
      });
    }
  };

  const nextSlide = () => {
    if (filteredVideos.length === 0) return;
    const nextIndex = (currentSlide + 1) % filteredVideos.length;
    setCurrentSlide(nextIndex);
    scrollToSlide(nextIndex);
  };

  const prevSlide = () => {
    if (filteredVideos.length === 0) return;
    const prevIndex = currentSlide === 0 ? filteredVideos.length - 1 : currentSlide - 1;
    setCurrentSlide(prevIndex);
    scrollToSlide(prevIndex);
  };

  const displayedJobs = jobs.filter(job => {
    const term = searchTerm.toLowerCase().trim();
    if (term) {
      const matchesSearch = (job.title || '').toLowerCase().includes(term)
        || (job.location || '').toLowerCase().includes(term)
        || (job.department || '').toLowerCase().includes(term)
        || (job.description || '').toLowerCase().includes(term)
        || (job.skills || []).some(s => s.toLowerCase().includes(term));
      if (!matchesSearch) return false;
    }
    if (jobFilters.workMode && job.workMode !== jobFilters.workMode) return false;
    if (jobFilters.experienceLevel && job.experienceLevel !== jobFilters.experienceLevel) return false;
    if (jobFilters.employmentType && job.employmentType !== jobFilters.employmentType) return false;
    return true;
  });

  const hasActiveFilters = searchTerm || jobFilters.workMode || jobFilters.experienceLevel || jobFilters.employmentType;

  const handleTalentSubmit = async (e) => {
    e.preventDefault();
    if (!talentForm.name || !talentForm.email || !talentForm.phone || !talentForm.area) return;
    setSubmittingTalent(true);
    try {
      const formData = new FormData();
      formData.append('name', talentForm.name);
      formData.append('email', talentForm.email);
      formData.append('phone', talentForm.phone);
      formData.append('areaOfInterest', talentForm.area);
      formData.append('message', talentForm.message);
      if (talentForm.resume) formData.append('resume', talentForm.resume);
      await fetch('/api/jobs/talent-pool', { method: 'POST', body: formData });
      setTalentSubmitted(true);
    } catch (err) {
      console.error('Talent pool submission failed:', err);
    } finally {
      setSubmittingTalent(false);
    }
  };

  const loadData = async () => {
    try {
      const [jobsRes, settingsRes] = await Promise.all([
        fetch('/api/jobs/public'),
        fetch('/api/company/settings')
      ]);

      const jobsData = await jobsRes.json();
      const settingsData = await settingsRes.json();

      setJobs(jobsData.jobs || []);
      setSettings(settingsData.settings);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper: "Posted 3 days ago", "Posted 2 weeks ago", etc.
  const getTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Posted today';
    if (diffDays === 1) return 'Posted yesterday';
    if (diffDays < 7) return `Posted ${diffDays} days ago`;
    if (diffDays < 14) return 'Posted 1 week ago';
    if (diffDays < 30) return `Posted ${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 60) return 'Posted 1 month ago';
    return `Posted ${Math.floor(diffDays / 30)} months ago`;
  };

  // Helper: Format deadline as "Mar 11, 2026"
  const formatDeadline = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Helper: Check if deadline is within 7 days
  const isDeadlineSoon = (dateStr) => {
    if (!dateStr) return false;
    const deadline = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((deadline - now) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  // Helper: Format salary range
  const formatSalary = (range) => {
    if (!range || (!range.min && !range.max)) return null;
    const currency = range.currency || 'INR';
    const symbol = { USD: '$', EUR: '€', GBP: '£', INR: '₹' }[currency] || currency + ' ';

    const formatNum = (n) => {
      if (n >= 100000) return `${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)}L`;
      if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
      return n.toString();
    };

    if (range.min && range.max) return `${symbol}${formatNum(range.min)} - ${symbol}${formatNum(range.max)}`;
    if (range.min) return `${symbol}${formatNum(range.min)}+`;
    if (range.max) return `Up to ${symbol}${formatNum(range.max)}`;
    return null;
  };

  return (
    <>
      <Helmet>
        <title>SecureLayer7 Careers - Join the Future of Cybersecurity</title>
        <meta name="description" content="SecHire is the official hiring platform of SecureLayer7 — a dedicated space to find, assess, and hire security talent. Explore cybersecurity careers in penetration testing, red team operations, security consulting, and more." />
        <link rel="canonical" href="https://sechire.net/" />
      </Helmet>

      <main className="public-home">
        <PublicHeader jobCount={jobs.length} />

        {/* Hero: written for a candidate, with the live roles beside it */}
        {settings && (
          <section className="hero">
            <div className="hero-copy">
              <p className="hero-kicker">{settings.hero?.title || 'SecureLayer7'} Careers</p>
              <h1>{settings.hero?.headline || 'Build your career in offensive security.'}</h1>
              <p className="description">
                {settings.hero?.careersDescription || 'Pentesters, red teamers and researchers who publish CVEs, speak at Black Hat and test for 1,000+ clients. Join the team in Pune, Thane or remote.'}
              </p>
              <div className="hero-actions">
                <a href="#jobs" className="hero-cta">See open roles</a>
                <a href="#talent-pool" className="hero-cta-secondary">Join the talent pool</a>
              </div>
              {jobs.length > 0 && (
                <p className="hero-meta">
                  {jobs.length} open {jobs.length === 1 ? 'role' : 'roles'}
                  <span className="hero-meta-sep">/</span>
                  {Array.from(new Set(jobs.map(j => (j.location || '').split(/[\/,]/)[0].trim()).filter(Boolean))).slice(0, 3).join(', ')}
                  <span className="hero-meta-sep">/</span>
                  {(() => { const modes = Array.from(new Set(jobs.map(j => j.workMode).filter(Boolean))).map(m => m === 'onsite' ? 'on-site' : m); const text = modes.length > 1 ? modes.slice(0, -1).join(', ') + ' and ' + modes[modes.length - 1] : modes.join(''); return text.charAt(0).toUpperCase() + text.slice(1); })()}
                </p>
              )}
            </div>
            {jobs.length > 0 && (
              <aside className="hero-roles" aria-label="Latest roles">
                <div className="hero-roles-head">
                  <span>Open now</span>
                  <a href="#jobs">All {jobs.length} roles</a>
                </div>
                {jobs.slice(0, 4).map(job => (
                  <a key={job._id} className="hero-role" href={`/jobs/${job.slug || job._id}`} onClick={(e) => { e.preventDefault(); navigate(`/jobs/${job.slug || job._id}`); }}>
                    <span className="hero-role-title">{job.title}</span>
                    <span className="hero-role-meta">{job.location}{job.experienceLevel ? ` / ${job.experienceLevel}` : ''}</span>
                  </a>
                ))}
              </aside>
            )}
          </section>
        )}

        {/* Trust Bar - Company credibility signals */}
        <section className="trust-bar">
          <div className="trust-stats">
            <div className="trust-stat">
              <span className="trust-number">1,000+</span>
              <span className="trust-label">Clients Secured</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-stat">
              <span className="trust-number">30+</span>
              <span className="trust-label">Countries</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-stat">
              <span className="trust-number">40,000+</span>
              <span className="trust-label">Vulnerabilities Found</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-stat">
              <span className="trust-number">$36M+</span>
              <span className="trust-label">Potential Losses Saved</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-stat">
              <span className="trust-number">10+</span>
              <span className="trust-label">Years in Business</span>
            </div>
          </div>
          <div className="trust-certs">
            <span className="trust-cert">CREST Accredited</span>
            <span className="trust-cert-dot">·</span>
            <span className="trust-cert">Published CVE Research</span>
            <span className="trust-cert-dot">·</span>
            <span className="trust-cert">Black Hat USA Presenter</span>
            <span className="trust-cert-dot">·</span>
            <a href="https://securelayer7.net" target="_blank" rel="noopener noreferrer" className="trust-cert-link">securelayer7.net →</a>
          </div>
        </section>

        {/* Why Join */}
        {settings?.whyJoin?.enabled && settings.whyJoin.points?.length > 0 && (
          <section className="why-join">
            <h2>{settings.whyJoin.title || 'Why Join Us'}</h2>
            <div className="points">
              {settings.whyJoin.points.map((point, idx) => (
                <div key={idx} className="point">
                  <h3>{point.title}</h3>
                  <p>{point.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Employee Spotlight */}
        {settings?.employeeSpotlight?.enabled && filteredVideos.length > 0 && (
          <section className="employee-spotlight">
            <h2>{settings.employeeSpotlight.title || 'Employee Spotlight'}</h2>
            {filteredVideos.length > 1 && filteredVideos.length <= 3 && (
              <div className="slider-controls">
                <button onClick={prevSlide} className="slider-arrow prev" aria-label="Previous">
                  ‹
                </button>
                <div className="slider-dots">
                  {filteredVideos.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => { setCurrentSlide(idx); scrollToSlide(idx); }}
                      className={`slider-dot ${idx === currentSlide ? 'active' : ''}`}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>
                <button onClick={nextSlide} className="slider-arrow next" aria-label="Next">
                  ›
                </button>
              </div>
            )}
            {filteredVideos.length >= 4 && (
              <div className="grid-scroll-indicators">
                {filteredVideos.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      const videoCard = sliderRef.current?.children[idx];
                      if (videoCard) {
                        videoCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      }
                    }}
                    className="scroll-indicator-btn"
                    aria-label={`Scroll to video ${idx + 1}`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            )}
            <div
              ref={sliderRef}
              className={`spotlight-videos
                ${filteredVideos.length > 1 && filteredVideos.length <= 3 ? 'slider' : ''}
                ${filteredVideos.length === 4 || filteredVideos.length === 5 || filteredVideos.length === 6 ? 'grid-2-rows' : ''}
                ${filteredVideos.length >= 7 ? 'grid-multi-rows' : ''}
                video-count-${filteredVideos.length}`}
            >
              {filteredVideos.map((video, idx) => {
                  // Detect platform and create appropriate embed
                  let embedUrl = video.videoUrl;
                  let isLinkedIn = false;
                  let linkedInPostId = '';
                  let isVimeo = false;

                  // YouTube detection
                  if (video.videoUrl?.includes('youtube.com') || video.videoUrl?.includes('youtu.be')) {
                    const match = video.videoUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
                    if (match && match[1]) {
                      embedUrl = `https://www.youtube.com/embed/${match[1]}`;
                    }
                  }
                  // Vimeo detection
                  else if (video.videoUrl?.includes('vimeo.com')) {
                    isVimeo = true;
                    const match = video.videoUrl.match(/vimeo\.com\/(\d+)/);
                    if (match && match[1]) {
                      embedUrl = `https://player.vimeo.com/video/${match[1]}`;
                    }
                  }
                  // LinkedIn detection
                  else if (video.videoUrl?.includes('linkedin.com')) {
                    isLinkedIn = true;
                    // Extract activity ID from LinkedIn URL
                    const match = video.videoUrl.match(/urn:li:activity:(\d+)/);
                    if (match && match[1]) {
                      linkedInPostId = match[1];
                    }
                  }

                  return (
                    <article key={idx} className="spotlight-video">
                      <div className="video-wrapper">
                        {isLinkedIn ? (
                          <div className="linkedin-placeholder">
                            <div className="linkedin-icon">
                              <svg width="64" height="64" viewBox="0 0 24 24" fill="#0077b5">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                              </svg>
                            </div>
                            <h3 className="linkedin-title">LinkedIn Post</h3>
                            <p className="linkedin-description">
                              {video.title || 'View this post on LinkedIn'}
                            </p>
                            <a
                              href={video.videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="linkedin-view-btn"
                            >
                              View on LinkedIn →
                            </a>
                          </div>
                        ) : (
                          <iframe
                            src={`${embedUrl}${embedUrl.includes('?') ? '&' : '?'}autoplay=1&mute=1&loop=1&playlist=${embedUrl.split('/').pop()}`}
                            title={video.title || `${video.employeeName} - ${video.employeeRole}`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        )}
                      </div>
                      <div className="video-info">
                        <h3>{video.employeeName}</h3>
                        <p className="employee-role">{video.employeeRole}</p>
                        {video.title && !isLinkedIn && <p className="video-title">{video.title}</p>}
                      </div>
                    </article>
                  );
                })}
            </div>
          </section>
        )}

        {/* FIREWALL Values */}
        {/* FIREWALL values: two courses of bricks, FIRE over WALL */}
        <section className="firewall-values" aria-label="Our values">
          <div className="firewall-inner">
            <div className="firewall-head">
              <h2 className="firewall-title">Eight bricks. One wall.</h2>
              <p className="firewall-lede">The first letters spell what we are built on. Read across each course.</p>
            </div>
            <div className="firewall-wall">
              {FIREWALL_VALUES.map((v, i) => (
                <div className={`value-brick course-${i < 4 ? 'top' : 'bottom'}`} key={v.letter + v.title}>
                  <div className="value-brick-head">
                    <span className="value-letter">{v.letter}</span>
                    <span className="value-badge"><ValueIcon name={v.icon} /></span>
                  </div>
                  <h3 className="value-title">{v.title}</h3>
                  {v.lines.map((line) => <p className="value-line" key={line}>{line}</p>)}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Job Listings */}
        <section id="jobs" className="jobs-section">
          <h2>Open Positions</h2>

          {/* Search & Filters */}
          {!loading && (
            <div className="jobs-search-bar">
              <div className="search-input-wrap">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search by role, skill, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="jobs-search-input"
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')} className="search-clear" aria-label="Clear search">&times;</button>
                )}
              </div>
              <div className="jobs-filter-row">
                <select value={jobFilters.workMode} onChange={(e) => setJobFilters(f => ({ ...f, workMode: e.target.value }))} className="jobs-filter-select">
                  <option value="">All Work Modes</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="onsite">On-site</option>
                </select>
                <select value={jobFilters.experienceLevel} onChange={(e) => setJobFilters(f => ({ ...f, experienceLevel: e.target.value }))} className="jobs-filter-select">
                  <option value="">All Levels</option>
                  <option value="entry">Entry</option>
                  <option value="junior">Junior</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior</option>
                  <option value="lead">Lead</option>
                </select>
                <select value={jobFilters.employmentType} onChange={(e) => setJobFilters(f => ({ ...f, employmentType: e.target.value }))} className="jobs-filter-select">
                  <option value="">All Types</option>
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                </select>
                {hasActiveFilters && (
                  <button onClick={() => { setSearchTerm(''); setJobFilters({ workMode: '', experienceLevel: '', employmentType: '' }); }} className="jobs-clear-filters">
                    Clear
                  </button>
                )}
              </div>
              {hasActiveFilters && (
                <div className="jobs-results-count">
                  {displayedJobs.length} {displayedJobs.length === 1 ? 'position' : 'positions'} found
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="loading">Loading positions...</div>
          ) : displayedJobs.length > 0 ? (
            <div className="jobs-list">
              {displayedJobs.map((job) => {
                const postedDate = job.publishedAt || job.createdAt;
                const postedAgo = postedDate ? getTimeAgo(new Date(postedDate)) : null;
                const deadline = job.applicationDeadline || job.expiresAt;
                const deadlineStr = deadline ? formatDeadline(new Date(deadline)) : null;
                const salaryStr = formatSalary(job.salaryRange);

                return (
                  <article key={job._id} className="job-item" onClick={() => navigate(`/jobs/${job.slug || job._id}`)}>
                    <div className="job-header">
                      <div>
                        <h3>{job.title}</h3>
                        {job.department && <span className="job-department">{job.department}</span>}
                      </div>
                      <span className="job-location">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: '-2px' }}>
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                        </svg>
                        {job.location}
                      </span>
                    </div>
                    <div className="job-tags">
                      <span className="job-tag tag-work-mode">{job.workMode}</span>
                      <span className="job-tag tag-type">{job.employmentType?.replace('-', ' ')}</span>
                      <span className="job-tag tag-level">{job.experienceLevel} level</span>
                      {salaryStr && <span className="job-tag tag-salary">{salaryStr}</span>}
                    </div>
                    <div className="job-footer">
                      <div className="job-footer-left">
                        {postedAgo && (
                          <span className="job-posted">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: '-1px' }}>
                              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                            </svg>
                            {postedAgo}
                          </span>
                        )}
                        {deadlineStr && (
                          <span className={`job-deadline ${isDeadlineSoon(deadline) ? 'deadline-soon' : ''}`}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: '-1px' }}>
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                            Apply by {deadlineStr}
                          </span>
                        )}
                      </div>
                      <span className="job-apply-hint">View & Apply →</span>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="no-jobs-filtered">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <p>{hasActiveFilters ? 'No positions match your search.' : 'No open positions at this time.'}</p>
              {hasActiveFilters && (
                <button onClick={() => { setSearchTerm(''); setJobFilters({ workMode: '', experienceLevel: '', employmentType: '' }); }} className="jobs-clear-filters" style={{ margin: '0.5rem auto' }}>
                  Clear filters
                </button>
              )}
            </div>
          )}

          {/* Talent Pool / General Interest */}
          <div id="talent-pool" className="talent-pool-section">
            {!showTalentForm && !talentSubmitted && (
              <div className="talent-pool-cta">
                <p>Can't find a role that fits?</p>
                <button onClick={() => setShowTalentForm(true)} className="talent-pool-btn">
                  Join our Talent Pool
                </button>
                <span className="talent-pool-hint">We'll reach out when a matching role opens.</span>
              </div>
            )}

            {talentSubmitted && (
              <div className="talent-pool-success">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <p>You're on our radar! We'll notify you when a matching position opens.</p>
              </div>
            )}

            {showTalentForm && !talentSubmitted && (
              <form className="talent-pool-form" onSubmit={handleTalentSubmit}>
                <h3>Join Our Talent Pool</h3>
                <p className="talent-form-desc">Tell us what you're looking for and we'll reach out when there's a match.</p>
                <div className="talent-form-grid">
                  <input type="text" placeholder="Full Name *" required value={talentForm.name} onChange={e => setTalentForm(f => ({ ...f, name: e.target.value }))} />
                  <input type="email" placeholder="Email *" required value={talentForm.email} onChange={e => setTalentForm(f => ({ ...f, email: e.target.value }))} />
                  <input type="tel" placeholder="Phone *" required value={talentForm.phone} onChange={e => setTalentForm(f => ({ ...f, phone: e.target.value }))} />
                  <select required value={talentForm.area} onChange={e => setTalentForm(f => ({ ...f, area: e.target.value }))}>
                    <option value="">Area of Interest *</option>
                    <option value="penetration-testing">Penetration Testing</option>
                    <option value="red-team">Red Team Operations</option>
                    <option value="security-consulting">Security Consulting</option>
                    <option value="source-code-review">Source Code Review</option>
                    <option value="cloud-security">Cloud Security</option>
                    <option value="appsec">Application Security</option>
                    <option value="devsecops">DevSecOps</option>
                    <option value="project-management">Project Management</option>
                    <option value="sales-marketing">Sales & Marketing</option>
                    <option value="engineering">Software Engineering</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <textarea placeholder="Anything else you'd like us to know? (experience, certifications, preferred location, etc.)" value={talentForm.message} onChange={e => setTalentForm(f => ({ ...f, message: e.target.value }))} rows="3" />
                <label className="talent-resume-label">
                  <span>{talentForm.resume ? talentForm.resume.name : 'Attach Resume (optional)'}</span>
                  <input type="file" accept=".pdf,.doc,.docx" onChange={e => setTalentForm(f => ({ ...f, resume: e.target.files[0] || null }))} style={{ display: 'none' }} />
                </label>
                <div className="talent-form-actions">
                  <button type="button" onClick={() => setShowTalentForm(false)} className="talent-cancel-btn">Cancel</button>
                  <button type="submit" disabled={submittingTalent} className="talent-submit-btn">
                    {submittingTalent ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>

        <PublicFooter />
      </main>
    </>
  );
};

export default PublicHome;
